import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { getSocket } from "../../utils/socket";
import { motion } from "framer-motion";

const AdminCoinSettings = () => {
  const { fetchWithRetry, user, isLoading } = useAuth();
  const navigate = useNavigate();
  const [exchangeRates, setExchangeRates] = useState([]);
  const [filteredRates, setFilteredRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notificationMessage, setNotificationMessage] = useState(null);
  const [newExchangeRate, setNewExchangeRate] = useState({ currency: "VND", rate: 0 });

  // State cho tìm kiếm, lọc, sắp xếp, phân trang
  const [searchTerm, setSearchTerm] = useState("");
  const [filterCurrency, setFilterCurrency] = useState("");
  const [sortField, setSortField] = useState("updatedAt");
  const [sortOrder, setSortOrder] = useState("desc");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Tải danh sách giá trị quy đổi
  const loadExchangeRates = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const res = await fetchWithRetry({ method: "get", url: "/api/admin/coins/exchange-rate?currency=VND" });

      // Log dữ liệu trả về để kiểm tra
      console.log("Exchange Rates Data:", res.data);

      const ratesData = Array.isArray(res.data) ? res.data : [res.data];
      setExchangeRates(ratesData);
      setFilteredRates(ratesData);

      if (ratesData.length === 0) {
        setError("Không có giá trị quy đổi nào được tìm thấy.");
      }
    } catch (error) {
      console.error("Lỗi khi tải danh sách giá trị quy đổi:", error);
      if (error.message.includes("500")) {
        setError("Lỗi server khi tải danh sách giá trị quy đổi. Vui lòng thử lại sau!");
      } else if (error.message.includes("401")) {
        setError("Phiên làm việc hết hạn. Vui lòng đăng nhập lại.");
      } else {
        setError("Không thể tải danh sách giá trị quy đổi!");
      }
    } finally {
      setLoading(false);
    }
  }, [fetchWithRetry]);

  // Xử lý thiết lập giá trị quy đổi
  const handleSetExchangeRate = async (e) => {
    e.preventDefault();
    try {
      const response = await fetchWithRetry({
        method: "post",
        url: "/api/admin/coins/set-exchange-rate",
        data: newExchangeRate,
      });
      setNotificationMessage(response.data.message || "Thiết lập giá trị quy đổi thành công!");
      setNewExchangeRate({ currency: "VND", rate: 0 });
      loadExchangeRates();
      setTimeout(() => setNotificationMessage(null), 3000);
    } catch (error) {
      console.error("Lỗi khi thiết lập giá trị quy đổi:", error);
      if (error.message.includes("500")) {
        setError("Lỗi server khi thiết lập giá trị quy đổi. Vui lòng thử lại sau!");
      } else if (error.message.includes("401")) {
        setError("Phiên làm việc hết hạn. Vui lòng đăng nhập lại.");
      } else {
        setError(error.response?.data?.message || "Không thể thiết lập giá trị quy đổi!");
      }
    }
  };

  // Lọc, sắp xếp giá trị quy đổi
  useEffect(() => {
    let filtered = [...exchangeRates];

    if (searchTerm) {
      const lowerSearchTerm = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (rate) =>
          rate.currency.toLowerCase().includes(lowerSearchTerm) ||
          rate.rate.toString().includes(lowerSearchTerm)
      );
    }

    if (filterCurrency) {
      filtered = filtered.filter((rate) => rate.currency === filterCurrency);
    }

    filtered.sort((a, b) => {
      const fieldA = a[sortField] || "";
      const fieldB = b[sortField] || "";
      if (sortField === "updatedAt") {
        return sortOrder === "asc" ? new Date(fieldA) - new Date(fieldB) : new Date(fieldB) - new Date(fieldA);
      } else if (sortField === "rate") {
        return sortOrder === "asc" ? fieldA - fieldB : fieldB - fieldA;
      } else {
        return sortOrder === "asc"
          ? fieldA.toString().localeCompare(fieldB.toString())
          : fieldB.toString().localeCompare(fieldA.toString());
      }
    });

    setFilteredRates(filtered);
  }, [exchangeRates, searchTerm, filterCurrency, sortField, sortOrder]);

  // Phân trang
  const totalItems = filteredRates.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentRates = filteredRates.slice(startIndex, endIndex);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  // Kiểm tra quyền truy cập và tải dữ liệu
  useEffect(() => {
    if (isLoading) return;
    if (!user || (user.role !== "admin" && user.role !== "mod")) {
      navigate("/login");
      return;
    }
    loadExchangeRates();

    const socket = getSocket();
    socket.on("notification", (notification) => {
      if (notification.type === "exchange_rate_updated") {
        setNotificationMessage(`Giá trị quy đổi đã được cập nhật: ${notification.message}`);
        loadExchangeRates();
        setTimeout(() => setNotificationMessage(null), 3000);
      }
    });

    return () => {
      socket.off("notification");
    };
  }, [user, navigate, loadExchangeRates, isLoading]);

  if (loading) return <p className="text-center text-gray-500">Đang tải...</p>;

  return (
    <div className="p-6 min-h-screen bg-gray-50">
      <h2 className="text-3xl font-bold text-gray-800 mb-6">Thiết lập giá trị quy đổi Coin</h2>
      {error && (
        <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-lg flex items-center justify-between">
          <span>{error}</span>
          {error.includes("Phiên làm việc hết hạn") ? (
            <button
              onClick={() => navigate("/login")}
              className="ml-4 px-4 py-1 bg-red-500 text-white rounded-lg hover:bg-red-600 transition duration-200"
            >
              Đăng nhập lại
            </button>
          ) : (
            <button
              onClick={() => setError(null)}
              className="ml-4 px-4 py-1 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition duration-200"
            >
              Đóng
            </button>
          )}
        </div>
      )}

      {notificationMessage && (
        <div className="mb-4 p-4 bg-green-100 text-green-700 rounded-lg">{notificationMessage}</div>
      )}

      {/* Form thiết lập giá trị quy đổi */}
      <div className="mb-8">
        <h3 className="text-xl font-semibold text-gray-800 mb-4">Thiết lập giá trị quy đổi mới</h3>
        <form onSubmit={handleSetExchangeRate} className="p-6 bg-white rounded-xl shadow-lg">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-gray-700 font-medium mb-2">Loại tiền tệ</label>
              <select
                value={newExchangeRate.currency}
                onChange={(e) => setNewExchangeRate({ ...newExchangeRate, currency: e.target.value })}
                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="VND">VND</option>
                <option value="USD">USD</option>
              </select>
            </div>
            <div>
              <label className="block text-gray-700 font-medium mb-2">Giá trị quy đổi (1 Coin = ?)</label>
              <input
                type="number"
                value={newExchangeRate.rate}
                onChange={(e) => setNewExchangeRate({ ...newExchangeRate, rate: parseFloat(e.target.value) })}
                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Nhập giá trị quy đổi"
                required
              />
            </div>
          </div>
          <button
            type="submit"
            className="mt-4 px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition duration-200"
          >
            Lưu
          </button>
        </form>
      </div>

      {/* Danh sách giá trị quy đổi */}
      {filteredRates.length > 0 && (
        <>
          <div className="mb-6 flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <input
                type="text"
                placeholder="Tìm kiếm theo loại tiền tệ, giá trị..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="flex-1 min-w-[150px]">
              <select
                value={filterCurrency}
                onChange={(e) => setFilterCurrency(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Lọc theo loại tiền tệ</option>
                <option value="VND">VND</option>
                <option value="USD">USD</option>
              </select>
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
            className="overflow-x-auto"
          >
            <table className="w-full bg-white rounded-lg shadow-lg">
              <thead>
                <tr className="bg-blue-500 text-white">
                  <th className="p-4 text-left cursor-pointer" onClick={() => handleSort("currency")}>
                    Loại tiền tệ {sortField === "currency" && (sortOrder === "asc" ? "↑" : "↓")}
                  </th>
                  <th className="p-4 text-left cursor-pointer" onClick={() => handleSort("rate")}>
                    Giá trị quy đổi {sortField === "rate" && (sortOrder === "asc" ? "↑" : "↓")}
                  </th>
                  <th className="p-4 text-left cursor-pointer" onClick={() => handleSort("updatedAt")}>
                    Ngày cập nhật {sortField === "updatedAt" && (sortOrder === "asc" ? "↑" : "↓")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {currentRates.map((rate) => (
                  <tr key={rate._id} className="border-b hover:bg-gray-50 transition duration-200">
                    <td className="p-4">{rate.currency}</td>
                    <td className="p-4">{rate.rate.toLocaleString()}</td>
                    <td className="p-4">{new Date(rate.updatedAt).toLocaleString("vi-VN")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </motion.div>

          <div className="mt-6 flex justify-between items-center">
            <div>
              <label className="mr-2">Hiển thị:</label>
              <select
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="p-2 border border-gray-300 rounded-lg"
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
              <span className="ml-2">mục/trang</span>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-4 py-1 bg-blue-500 text-white rounded-lg disabled:bg-gray-300"
              >
                Trước
              </button>
              <span className="px-4 py-1">
                Trang {currentPage} / {totalPages}
              </span>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-4 py-1 bg-blue-500 text-white rounded-lg disabled:bg-gray-300"
              >
                Sau
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AdminCoinSettings;